import type { Component } from 'solid-js'
import { createSignal, onMount, For, Show } from 'solid-js'
import { listTemplates } from '@services/templates'
import { syncUsers, rebuildUserVenueAccess } from '@services/users'
import { clearVenueCache } from '@services/venues'
import { syncShortcodes, getShortcodes } from '@services/shortcodes'

const Home: Component = () => {
  const [templates, setTemplates] = createSignal<string[] | null>(null)
  const [syncStatus, setSyncStatus] = createSignal<string | null>(null)
  const [isSyncing, setIsSyncing] = createSignal(false)
  const [resyncStatus, setResyncStatus] = createSignal<string | null>(null)
  const [isResyncing, setIsResyncing] = createSignal(false)
  const [clerkUserId, setClerkUserId] = createSignal('')
  const [cacheStatus, setCacheStatus] = createSignal<string | null>(null)
  const [isClearingCache, setIsClearingCache] = createSignal(false)
  const [tenantRef, setTenantRef] = createSignal('')
  const [venueRef, setVenueRef] = createSignal('')
  const [shortcodesStatus, setShortcodesStatus] = createSignal<string | null>(null)
  const [isSyncingShortcodes, setIsSyncingShortcodes] = createSignal(false)
  const [shortCodes, setShortCodes] = createSignal<Record<string, string> | null>(null)
  const [isLoadingShortCodes, setIsLoadingShortCodes] = createSignal(false)

  onMount(async () => {
    const t = await listTemplates()
    setTemplates(t)
    await loadShortCodes()
  })

  const handleSyncUsers = async () => {
    setIsSyncing(true)
    setSyncStatus(null)

    const result = await syncUsers()

    setIsSyncing(false)

    if (result) {
      setSyncStatus(result.message)
    } else {
      setSyncStatus('Failed to start user synchronization')
    }
  }

  const handleResyncUser = async () => {
    const clerkId = clerkUserId().trim()
    if (!clerkId) {
      setResyncStatus('Please enter a Clerk user ID')
      return
    }

    setIsResyncing(true)
    setResyncStatus(null)

    const result = await rebuildUserVenueAccess(clerkId)

    setIsResyncing(false)

    if (result) {
      setResyncStatus(
        `Rebuild complete for ${result.clerk_user_id}. venues_granted=${result.venues_granted.length}`
      )
      setClerkUserId('')
    } else {
      setResyncStatus('Failed to rebuild user venue access')
    }
  }

  const handleClearVenueCache = async () => {
    const tenant = tenantRef().trim()
    const venue = venueRef().trim()

    if (!tenant || !venue) {
      setCacheStatus('Please enter both tenant reference and venue reference')
      return
    }

    setIsClearingCache(true)
    setCacheStatus(null)

    const result = await clearVenueCache({ tenant_ref: tenant, venue_ref: venue })

    setIsClearingCache(false)

    if (result) {
      setCacheStatus(result.message)
      // Clear the form
      setTenantRef('')
      setVenueRef('')
    } else {
      setCacheStatus('Failed to clear venue cache')
    }
  }

  const handleSyncShortcodes = async () => {
    setIsSyncingShortcodes(true)
    setShortcodesStatus(null)

    const result = await syncShortcodes()

    setIsSyncingShortcodes(false)

    if (result) {
      setShortcodesStatus(result.message)
      // Reload short codes after sync
      await loadShortCodes()
    } else {
      setShortcodesStatus('Failed to sync shortcodes')
    }
  }

  const loadShortCodes = async () => {
    setIsLoadingShortCodes(true)
    const result = await getShortcodes()
    setIsLoadingShortCodes(false)
    if (result) {
      setShortCodes(result.short_codes)
    }
  }

  return (
    <div class="grow flex items-center justify-center p-6">
      <div class="w-full max-w-2xl space-y-6">
        {/* User Sync Section */}
        <div class="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 class="text-2xl font-semibold text-white mb-4">User Management</h2>
          <div class="space-y-4">
            <button
              onClick={handleSyncUsers}
              disabled={isSyncing()}
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-md transition-colors"
            >
              {isSyncing() ? 'Syncing Users...' : 'Sync Users from Clerk'}
            </button>
            <Show when={syncStatus()}>
              <p class="text-sm text-gray-300">{syncStatus()}</p>
            </Show>

            <div class="pt-4 border-t border-zinc-800">
              <label for="clerk-user-id" class="block text-sm font-medium text-gray-300 mb-2">
                Clerk User ID for Resync
              </label>
              <input
                id="clerk-user-id"
                type="text"
                value={clerkUserId()}
                onInput={(e) => setClerkUserId(e.currentTarget.value)}
                placeholder="e.g., usr_123abc"
                class="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleResyncUser}
                disabled={isResyncing()}
                class="mt-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 disabled:cursor-not-allowed text-white rounded-md transition-colors"
              >
                {isResyncing() ? 'Resyncing User...' : 'Resync User Venue Access'}
              </button>
              <Show when={resyncStatus()}>
                <p class="text-sm text-gray-300">{resyncStatus()}</p>
              </Show>
            </div>
          </div>
        </div>

        {/* Venue Cache Management Section */}
        <div class="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 class="text-2xl font-semibold text-white mb-4">Venue Cache Management</h2>
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="tenant-ref" class="block text-sm font-medium text-gray-300 mb-2">
                  Tenant Reference
                </label>
                <input
                  id="tenant-ref"
                  type="text"
                  value={tenantRef()}
                  onInput={(e) => setTenantRef(e.currentTarget.value)}
                  placeholder="e.g., acme-bars-ltd"
                  class="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label for="venue-ref" class="block text-sm font-medium text-gray-300 mb-2">
                  Venue Reference
                </label>
                <input
                  id="venue-ref"
                  type="text"
                  value={venueRef()}
                  onInput={(e) => setVenueRef(e.currentTarget.value)}
                  placeholder="e.g., brighthelm"
                  class="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={handleClearVenueCache}
              disabled={isClearingCache()}
              class="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white rounded-md transition-colors"
            >
              {isClearingCache() ? 'Clearing Cache...' : 'Clear Venue Cache'}
            </button>
            <Show when={cacheStatus()}>
              <p class="text-sm text-gray-300">{cacheStatus()}</p>
            </Show>
          </div>
        </div>

        {/* Short Codes Management Section */}
        <div class="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 class="text-2xl font-semibold text-white mb-4">Short Codes Management</h2>
          <div class="space-y-4">
            <button
              onClick={handleSyncShortcodes}
              disabled={isSyncingShortcodes()}
              class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-md transition-colors"
            >
              {isSyncingShortcodes() ? 'Syncing Short Codes...' : 'Sync Short Codes'}
            </button>
            <Show when={shortcodesStatus()}>
              <p class="text-sm text-gray-300">{shortcodesStatus()}</p>
            </Show>
          </div>
        </div>

        {/* Current Short Codes Section */}
        <div class="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 class="text-2xl font-semibold text-white mb-4">Current Short Codes</h2>
          <Show when={isLoadingShortCodes()}>
            <p class="text-gray-300">Loading short codes...</p>
          </Show>
          <Show when={!isLoadingShortCodes() && shortCodes()}>
            <div class="space-y-2">
              <For each={Object.entries(shortCodes() ?? {})}>
                {([shortCode, venuePath]) => (
                  <div class="flex justify-between items-center py-2 px-3 bg-zinc-800 rounded-md">
                    <span class="text-white font-mono">{shortCode}</span>
                    <span class="text-gray-300 text-sm">{venuePath}</span>
                  </div>
                )}
              </For>
            </div>
          </Show>
          <Show when={!isLoadingShortCodes() && !shortCodes()}>
            <p class="text-gray-300">No short codes found. Try syncing first.</p>
          </Show>
        </div>

        <div class="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h2 class="text-2xl font-semibold text-white mb-4">Templates</h2>
          <Show when={templates() !== null} fallback={<p class="text-gray-300">Loading…</p>}>
            <For each={templates() ?? []}>
              {(t) => (
                <div class="py-2 px-3 border-b border-zinc-800 text-gray-200">{t}</div>
              )}
            </For>
          </Show>
        </div>
      </div>
    </div>
  )
}

export default Home
